import {CabReportItem, formatWaarde, REPORTED_VALUE_UNKNOWN, WaardeTypeEnum} from './index.js';

export const ICON_START: string      = '✳️';
export const ICON_RESULT: string     = '▶️';
export const ICON_SUB_RESULT: string = '◀️';
export const ICON_OK: string         = '✅';
export const ICON_ONBEKEND: string   = '🅾️';
export const ICON_UNDEFINED: string  = '⭕️';
export const ICON_ERROR: string      = '❌';
export const ICON_DOT: string        = '⏺️';

export enum EntryType {
    START,
    FIELD_ACCESS,
    ASSIGN,
    END,
    ERROR,
}

export class TraceEntry {
    entryType: EntryType;
    uuid?: string;
    fieldname?: string;
    valueIsUndefined: boolean;
    valueIsOnbekend: boolean;
    valueIsItem: boolean;
    value?: any;
    message?: string;
    exc?: any;

    public static jsonArrayRaw(json:string):TraceEntry[] {
        const array: any[] = JSON.parse(json);
        return array.map(obj => new TraceEntry(
            obj.entryType,
            obj.uuid,
            obj.fieldname,
            obj.valueIsUndefined,
            obj.valueIsOnbekend,
            obj.valueIsItem,
            obj.value,
            obj.message,
            obj.exc,
        ));
    }

    public static jsonArray(json: string): TraceEntry[] {
        try {
            return TraceEntry.jsonArrayRaw(json);
        } catch (e) {
            return [TraceEntry.error(undefined, `could not parse json: '${json}'`, e)];
            //could not parse json: '
            // {"entryType":4,"uuid":"eyvg5wBHYuzRq6myXf79pw","valueIsUndefined":true,"valueIsOnbekend":false,"valueIsItem":false,"message":"missing expression"}
            // '
        }
    }

    public static renderForLog(json: string): string {
        try {
            return TraceEntry.jsonArrayRaw(json).map(te => te.renderForLog()).join('\n')
        } catch (e) {
            return json;
        }
    }

    static start(reportItem: CabReportItem): TraceEntry {
        return TraceEntry.of(EntryType.START, reportItem.uuid, undefined, undefined, undefined, undefined, false);
    }

    static fieldAccess(reportItem: CabReportItem, fieldname: string, value: any, valueIsItem: boolean): TraceEntry {
        return TraceEntry.of(EntryType.FIELD_ACCESS, reportItem.uuid, fieldname, value, undefined, undefined, valueIsItem);
    }

    static assign(variable: string, value: any, valueIsItem: boolean): TraceEntry {
        return TraceEntry.of(EntryType.ASSIGN, undefined, variable, value, undefined, undefined, valueIsItem);
    }

    static end(reportItem: CabReportItem, value: any, valueIsItem: boolean): TraceEntry {
        return TraceEntry.of(EntryType.END, reportItem.uuid, undefined, value, undefined, undefined, valueIsItem);
    }

    static error(reportItem: CabReportItem | undefined, message: string, exc: any): TraceEntry {
        return TraceEntry.of(EntryType.ERROR, reportItem?.uuid, undefined, undefined, message, exc, false);
    }

    private static of(entryType: EntryType, uuid: string | undefined, fieldname: string | undefined, value: any | undefined, message: string | undefined, exc: any | undefined, valueIsItem: boolean): TraceEntry {
        return new TraceEntry(
            entryType,
            uuid,
            fieldname,
            value === undefined,
            value === REPORTED_VALUE_UNKNOWN,
            valueIsItem,
            value === undefined || value === REPORTED_VALUE_UNKNOWN ? undefined : typeof value === 'number' ? value : `${value}`,
            message,
            exc,
        );
    }

    constructor(entryType: EntryType,
        uuid: string | undefined,
        fieldname: string | undefined,
        valueIsUndefined: boolean,
        valueIsOnbekend: boolean,
        valueIsItem: boolean,
        value: any,
        message: string | undefined,
        exc: any,
    ) {
        this.entryType        = entryType;
        this.uuid             = uuid;
        this.fieldname        = fieldname;
        this.valueIsUndefined = valueIsUndefined;
        this.valueIsOnbekend  = valueIsOnbekend;
        this.valueIsItem      = valueIsItem;
        this.value            = value;
        this.message          = message;
        this.exc              = exc;
    }

    public equals(other: TraceEntry): boolean {
        return this.entryType === other.entryType
            && this.uuid === other.uuid
            && this.fieldname === other.fieldname
            && this.valueIsUndefined === other.valueIsUndefined
            && this.valueIsOnbekend === other.valueIsOnbekend
            && this.valueIsItem === other.valueIsItem
            && this.value === other.value
            && this.message === other.message
            && this.exc === other.exc
        ;
    }

    stringify(): string {
        return JSON.stringify(this);
    }

    public renderForLog(): string {
        switch (this.entryType) {
            case EntryType.START:
                return `${ICON_START} ${this.uuid}`;
            case EntryType.FIELD_ACCESS:
                return `${this.renderIcon()} ${(this.renderValue().padEnd(40))} ${ICON_SUB_RESULT} ${this.fieldname?.padEnd(40)} ${ICON_DOT} ${this.uuid}`;
            case EntryType.ASSIGN:
                return `${this.renderIcon()} ${(this.renderValue().padEnd(40))} ${ICON_SUB_RESULT} ${this.fieldname?.padEnd(40)}`;
            case EntryType.END:
                return `${ICON_RESULT} ${this.renderValue()}`;
            case EntryType.ERROR:
                if (this.exc) {
                    return `${ICON_ERROR} ${this.message} exc: ${this.exc instanceof Error ? String(this.exc) : this.exc.message}`;
                } else {
                    return `${ICON_ERROR} ${this.message}}`;
                }
        }
    }

    public renderValue(): string {
        return this.value === undefined ? `(undefined)` : this.value === REPORTED_VALUE_UNKNOWN ? `(onbekend)` : typeof this.value === 'number' ? formatWaarde(this.value, WaardeTypeEnum.decimaal) : `${this.value}`;
    }

    public renderIcon(): string {
        return this.value === REPORTED_VALUE_UNKNOWN ? ICON_ONBEKEND : this.value === undefined ? ICON_UNDEFINED : ICON_OK;
    }

    public getUuids(): string[] {
        const all: string[] = [];
        if (this.uuid) {
            all.push(this.uuid);
        }
        return all;
    }
}
